import React, { useState, useEffect, useRef } from "react";
import DataTable from "react-data-table-component";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { apiGet, apiPost, mediaUrl } from "../../Utils";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Btn, H3, H5 } from "../../AbstractElements";
import { useNavigate } from "react-router-dom";
import { Edit, Plus, Trash2, Disc, Tool, Eye, FileText, Clipboard } from 'react-feather';
import { Card, CardBody, Col, Button, Spinner, Container, Input, Row, Table, Badge, Modal, Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import Swal from 'sweetalert2';
import { useAuth } from "../../Context/AuthContext";
import { useConfig } from "../../Context/ConfigContext";

const ExpandedComponent = ({ data }: { data: any }) => {
    const { config } = useConfig();
    // Safe parse helper
    const parseItems = (items: any) => {
        if (!items) return [];
        if (Array.isArray(items)) return items;
        try {
            return JSON.parse(items);
        } catch (e) {
            return [];
        }
    };

    const parts = parseItems(data.parts_replaced);
    const labour = parseItems(data.labour_services);

    // Determine Type for badge
    const isHybrid = (parts.length > 0 || data.parts_vendor_name) && (labour.length > 0 || data.garage_name);

    return (
        <div className="p-3 bg-light border-bottom">
            <Row>
                <Col md={12} className="mb-2">
                    <strong style={{ color: '#000' }}>Description: </strong> <span style={{ color: '#000' }}>{data.description || 'No description'}</span>
                </Col>

                {/* INSPECTION SECTION */}
                {data.inspection_data && (
                    <Col md={12}>
                        <Card className="mb-2 shadow-sm border-start border-success border-3">
                            <CardBody className="p-3">
                                <H5 className="text-success mb-3"><Clipboard size={18} className="me-2" />Inspection Report</H5>
                                <Row className="g-2 small text-muted">
                                    {data.inspection_data.inspection_date && (
                                        <Col md={3}><strong>Date:</strong> {new Date(data.inspection_data.inspection_date).toLocaleDateString('en-GB')}</Col>
                                    )}
                                    {data.inspection_data.inspection_type && (
                                        <Col md={3}><strong>Type:</strong> {data.inspection_data.inspection_type}</Col>
                                    )}
                                    {data.inspection_data.inspection_status && (
                                        <Col md={3}><strong>Status:</strong>{' '}
                                            <Badge color={
                                                data.inspection_data.inspection_status === 'Passed' ? 'success' :
                                                    data.inspection_data.inspection_status === 'Failed' ? 'danger' :
                                                        data.inspection_data.inspection_status === 'In Progress' ? 'warning' : 'secondary'
                                            }>
                                                {data.inspection_data.inspection_status}
                                            </Badge>
                                        </Col>
                                    )}
                                    {data.inspection_data.vendor_name && (
                                        <Col md={6}><strong>Vendor:</strong> {data.inspection_data.vendor_name}</Col>
                                    )}
                                    {data.inspection_data.vendor_details && (
                                        <Col md={6}><strong>Details:</strong> {data.inspection_data.vendor_details}</Col>
                                    )}
                                    {(data.inspection_data.report_files || []).length > 0 && (
                                        <Col md={12}>
                                            <strong>Reports:</strong>{' '}
                                            {(data.inspection_data.report_files || []).map((file: string, i: number) => (
                                                <a key={i} href={`${process.env.REACT_APP_MEDIA_URL || ''}${file}`} target="_blank" rel="noopener noreferrer"
                                                    className="btn btn-outline-success btn-sm me-1 mt-1" style={{ fontSize: 11 }}>
                                                    <FileText size={12} className="me-1" /> File {i + 1}
                                                </a>
                                            ))}
                                        </Col>
                                    )}
                                </Row>
                            </CardBody>
                        </Card>
                    </Col>
                )}
                {/* Parts Only / Hybrid Parts Section */}
                {(parts.length > 0 || data.parts_vendor_name) && (
                    <Col md={isHybrid ? 6 : 12}>
                        <Card className="mb-2 shadow-sm border-start border-primary border-3">
                            <CardBody className="p-3">
                                <H5 className="text-primary mb-3"><Disc size={18} className="me-2" />Parts Details</H5>
                                <div className="mb-2 text-muted small">
                                    <div><strong>Vendor:</strong> {data.parts_vendor_name || 'N/A'}</div>
                                </div>
                                {parts.length > 0 ? (
                                    <Table size="sm" striped hover responsive className="mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Item Name</th>
                                                <th className="text-end">Cost ({config.currency})</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {parts.map((item: any, index: number) => {
                                                const name = typeof item === 'string' ? item : item.name;
                                                const amount = typeof item === 'string' ? '' : item.amount;
                                                return (
                                                    <tr key={index}>
                                                        <td>{name}</td>
                                                        <td className="text-end">{amount}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                ) : <div className="text-muted f-st-italic">No itemized parts.</div>}
                            </CardBody>
                        </Card>
                    </Col>
                )}

                {/* Labour Only / Hybrid Labour Section */}
                {(labour.length > 0 || data.garage_name) && (
                    <Col md={isHybrid ? 6 : 12}>
                        <Card className="mb-2 shadow-sm border-start border-warning border-3">
                            <CardBody className="p-3">
                                <H5 className="text-warning mb-3"><Tool size={18} className="me-2" />Labour / Service Details</H5>
                                <div className="mb-2 text-muted small">
                                    <div><strong>Garage:</strong> {data.garage_name || 'N/A'}</div>
                                </div>
                                {labour.length > 0 ? (
                                    <Table size="sm" striped hover responsive className="mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Service Description</th>
                                                <th className="text-end">Cost ({config.currency})</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {labour.map((item: any, index: number) => (
                                                <tr key={index}>
                                                    <td>{item.item_description}</td>
                                                    <td className="text-end">{item.unit_price}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                ) : <div className="text-muted f-st-italic">No itemized services.</div>}
                            </CardBody>
                        </Card>
                    </Col>
                )}
            </Row>
        </div>
    );
};

export default function ViewMaintenance() {
    const [viewModal, setViewModal] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [selectedMaintenance, setSelectedMaintenance] = useState<any>(null);
    const [activeTab, setActiveTab] = useState('1');

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [refresh, setRefresh] = useState(0);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortColumn, setSortColumn] = useState("");
    const [sortDirection, setSortDirection] = useState("");
    const prevSearch = useRef(searchTerm);
    const navigate = useNavigate();
    const { hasPermission } = useAuth();
    const { config } = useConfig();

    useEffect(() => {
        const isSearchChanged = prevSearch.current !== searchTerm;
        prevSearch.current = searchTerm;

        // Reset to page 1 when search changes
        if (isSearchChanged && currentPage !== 1) {
            setCurrentPage(1);
            return; // Exit early, let page change trigger next fetch
        }

        const delay = isSearchChanged ? 500 : 0;
        const timer = setTimeout(() => {
            fetchData(currentPage, perPage, searchTerm, sortColumn, sortDirection);
        }, delay);
        return () => clearTimeout(timer);
    }, [refresh, currentPage, perPage, searchTerm, sortColumn, sortDirection]);

    const fetchData = async (page: number, size: number, search: string = "", sortCol: string = "", sortDir: string = "") => {
        setLoading(true);
        try {
            const body: any = {
                page: page,
                per_page: size,
                search: search
            };

            if (sortCol) {
                body.sort_by = sortCol;
                body.sort_direction = sortDir;
            }

            const response: any = await apiPost("maintenance/getall", body);
            if (response && response.data) {
                setData(response.data);
                setTotalRows(response.total);
            } else {
                setData(response);
                setTotalRows(response.length);
            }
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSort = (column: any, sortDir: string) => {
        if (column.sortField) {
            setSortColumn(column.sortField);
            setSortDirection(sortDir);
            setCurrentPage(1);
        }
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handlePerRowsChange = async (newPerPage: number, page: number) => {
        setPerPage(newPerPage);
        setCurrentPage(page);
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
    };

    const handleAdd = () => {
        navigate(`${process.env.PUBLIC_URL}/add_maintenance`);
    };

    const handleEdit = (id: number) => {
        navigate(`${process.env.PUBLIC_URL}/edit_maintenance`, { state: { id: id } });
    };

    const handleDelete = async (id: number) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await apiPost("maintenance/delete", { id: id });
                    toast.success("Maintenance record deleted successfully", { autoClose: 1000 });
                    setRefresh(prev => prev + 1);
                } catch (error) {
                    toast.error("Failed to delete record", { autoClose: 1000 });
                }
            }
        });
    };

    const toggleViewModal = () => {
        if (viewModal) {
            handleClose();
        } else {
            setViewModal(true);
        }
    };

    const handleClose = () => {
        setIsClosing(true);
        setTimeout(() => {
            setViewModal(false);
            setIsClosing(false);
        }, 550);
    };
    const toggleTab = (tab: string) => {
        if (activeTab !== tab) setActiveTab(tab);
    }

    const handleView = (row: any) => {
        setSelectedMaintenance(row);
        setActiveTab('1');
        setViewModal(true);
    };


    const calculateProfileCompletion = (row: any) => {
        // ... (Optional: if we want a completion score, but not requested)
        return 100;
    };

    // Helper to parse JSON items safely
    const parseItems = (items: any) => {
        if (!items) return [];
        if (Array.isArray(items)) return items;
        try {
            return JSON.parse(items);
        } catch (e) {
            return [];
        }
    };

    const columns = React.useMemo(() => [
        {
            name: <div>Sl. No</div>,
            selector: (row: any, index?: number) => (currentPage - 1) * perPage + (index || 0) + 1,
            width: "7%",
        },
        {
            name: <div>Reg No</div>,
            selector: (row: any) => row.vehicle?.registration_number || 'N/A',
            sortable: true,
            sortField: "registration_number",
            cell: (row: any) => (
                <span className="text-primary">
                    {row.vehicle?.registration_number || 'N/A'}
                    {(row.vehicle?.deleted_at || row.deleted_at) && (
                        <span className="text-danger ms-1">(Deleted)</span>
                    )}
                </span>
            ),
            wrap: true,
        },
        {
            name: <div>Branch</div>,
            selector: (row: any) => row.branch?.branch_name || 'N/A',
            sortable: true,
            sortField: "branch_name",
            cell: (row: any) => (
                <span>
                    {row.branch?.branch_name || 'N/A'}
                    {row.branch?.deleted_at && (
                        <span className="text-danger ms-1">(Deleted)</span>
                    )}
                </span>

            ),
            wrap: true,
        },
        {
            name: <div>Type</div>,
            cell: (row: any) => {
                const hasParts = (row.parts_replaced && JSON.parse(JSON.stringify(row.parts_replaced)).length > 0) || row.parts_vendor_name;
                const hasLabour = (row.labour_services && JSON.parse(JSON.stringify(row.labour_services)).length > 0) || row.garage_name;

                if (hasParts && hasLabour) return <Badge color="dark">Hybrid</Badge>;
                if (hasLabour) return <Badge color="warning" className="text-dark">Labour</Badge>;
                return <Badge color="primary">Parts</Badge>;
            },
            width: "100px"
        },
        {
            name: <div>Vendor / Garage</div>,
            cell: (row: any) => {
                const parts = row.parts_vendor_name;
                const garage = row.garage_name;

                if (parts && garage) {
                    return (
                        <span>
                            <span className="fw-bold text-warning">Parts:</span> {parts} || <span className="fw-bold text-warning">Labour:</span> {garage}
                        </span>
                    );
                }
                if (parts) return <span><span className="fw-bold text-warning">Parts:</span> {parts}</span>;
                if (garage) return <span><span className="fw-bold text-warning">Labour:</span> {garage}</span>;

                return '-';
            },
            sortable: true,
            sortField: "garage_name",
            wrap: true,
        },

        {
            name: <div>Next Service</div>,
            selector: (row: any) => row.next_service_date ? row.next_service_date.split('T')[0] : '-',
            sortable: true,
            sortField: "next_service_date",
            cell: (row: any) => row.next_service_date ? new Date(row.next_service_date).toLocaleDateString('en-GB') : '-'
        },
        {
            name: <div>Cost ({config.currency})</div>,
            selector: (row: any) => row.total_cost || '-',
            sortable: true,
            sortField: "total_cost",
        },
        {
            name: <div>Invoice</div>,
            cell: (row: any) => row.invoice_document ? (
                <a
                    href={`${mediaUrl}${row.invoice_document}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-outline-info btn-sm"
                >
                    View
                </a>
            ) : 'N/A',
            sortable: false,
        },
        {
            name: <div>Status</div>,
            cell: (row: any) => {
                const status = row.status;
                let badgeClass = "bg-secondary";
                if (status === "Open" || status === "Pending") badgeClass = "bg-primary";
                else if (status === "In Progress") badgeClass = "bg-warning";
                else if (status === "Closed" || status === "Completed") badgeClass = "bg-success";

                return (
                    <span className={`badge ${badgeClass}`}>
                        {status}
                    </span>
                );
            },
            sortable: true,
            sortField: "status",
        },
        {
            name: <div>Actions</div>,
            cell: (row: any) => (
                <div className="d-flex justify-content-between gap-2">
                    {hasPermission('maintenance.view') && (
                        <Button size="sm" color="primary" onClick={() => handleView(row)} title="View Details">
                            <Eye size={14} />
                        </Button>
                    )}
                    {hasPermission('maintenance.edit') && (
                        <Button size="sm" color="info" onClick={() => handleEdit(row.id)} title="Edit">
                            <Edit size={14} />
                        </Button>
                    )}
                    {hasPermission('maintenance.delete') && (
                        <Button size="sm" color="danger" onClick={() => handleDelete(row.id)} title="Delete">
                            <Trash2 size={14} />
                        </Button>
                    )}
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
        },
    ], [currentPage, perPage]);

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: '#e0f2f1',
                color: '#00695c',
                minHeight: '50px',
                borderBottom: '1px solid #e0e0e0',
            },
        },
        headCells: {
            style: {
                fontSize: '15px',
                fontWeight: '600',
                paddingLeft: '16px',
                paddingRight: '16px',
            },
        },
        rows: {
            style: {
                minHeight: '50px',
                '&:not(:last-of-type)': {
                    borderBottomStyle: 'solid',
                    borderBottomWidth: '1px',
                    borderBottomColor: '#e0e0e0',
                },
            },
        },
    };

    return (
        <div className='page-body'>
            <Breadcrumbs pageTitle="Maintenance" parent="General" title="Maintenance" />
            <Container fluid>
                <Col xl={12} className="mb-lg-3">
                    <Card>
                        <CardBody>
                            <Col xs={12} className="mt-2 d-flex justify-content-between align-items-center mb-3">
                                <H3 className="text-primary">Maintenance Overview</H3>
                                {hasPermission('maintenance.add') && (
                                    <Btn color="primary" onClick={handleAdd}>
                                        <Plus size={16} /> Add Maintenance
                                    </Btn>
                                )}
                            </Col>

                            <Col xs={12} className="mb-3 d-flex justify-content-end">
                                <Input type="text" placeholder="Search..." value={searchTerm} onChange={handleSearchChange} style={{ maxWidth: "300px" }} />
                            </Col>
                            <div className="table-responsive">
                                <DataTable
                                    data={data}
                                    columns={columns}
                                    pagination
                                    paginationServer
                                    paginationTotalRows={totalRows}
                                    onChangePage={handlePageChange}
                                    onChangeRowsPerPage={handlePerRowsChange}
                                    onSort={handleSort}
                                    sortServer
                                    progressPending={loading}
                                    progressComponent={<div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '50vh' }}><Spinner color="primary" /></div>}
                                    highlightOnHover
                                    customStyles={customStyles}
                                    expandableRows
                                    expandableRowsComponent={ExpandedComponent}
                                />
                            </div>


                        </CardBody>
                    </Card>
                </Col>
            </Container>

            {/* View Maintenance Modal */}
            <Modal isOpen={viewModal} toggle={toggleViewModal} size="lg" centered contentClassName={isClosing ? "animate-3d-slit-out" : "animate-3d-slit"} fade={false}>
                <div style={{
                    background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                    padding: '1.5rem',
                    position: 'relative', width: '100%'
                }}>
                    <button
                        className="btn-close btn-close-white"
                        type="button"
                        onClick={handleClose}
                        style={{ position: 'absolute', top: '1rem', right: '1rem', opacity: 0.9 }}
                    ></button>

                    {selectedMaintenance && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                            <div style={{
                                width: '60px',
                                height: '60px',
                                borderRadius: '50%',
                                backgroundColor: 'rgba(255,255,255,0.2)',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                flexShrink: 0
                            }}>
                                <Tool size={30} color="white" />
                            </div>
                            <div style={{ flex: 1 }}>
                                <h3 style={{
                                    color: 'white',
                                    fontWeight: 700,
                                    fontSize: '1.5rem',
                                    margin: 0,
                                    marginBottom: '0.25rem'
                                }}>
                                    {selectedMaintenance.vehicle?.brand} {selectedMaintenance.vehicle?.model}
                                </h3>
                                <div style={{ display: 'flex', gap: '1rem', color: 'rgba(255,255,255,0.85)', fontSize: '0.9rem' }}>
                                    <span style={{ fontWeight: 600 }}>{selectedMaintenance.vehicle?.registration_number}</span>
                                    <span>•</span>
                                    <span>{selectedMaintenance?.service_date
                                        ? selectedMaintenance.service_date.split('T')[0]
                                        : 'N/A'}</span>
                                </div>
                            </div>
                            <div>
                                <span style={{
                                    backgroundColor: selectedMaintenance.status === 'Completed' ? 'rgba(16, 185, 129, 0.2)' : selectedMaintenance.status === 'Pending' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(245, 158, 11, 0.2)',
                                    color: selectedMaintenance.status === 'Completed' ? '#10b981' : selectedMaintenance.status === 'Pending' ? '#60a5fa' : '#f59e0b',
                                    padding: '0.5rem 1rem',
                                    margin: '0.5rem 1rem',
                                    borderRadius: '20px',
                                    fontSize: '0.875rem',
                                    fontWeight: 700,
                                    border: '1px solid rgba(255,255,255,0.2)',
                                    backdropFilter: 'blur(4px)'
                                }}>
                                    {selectedMaintenance.status}
                                </span>
                            </div>
                        </div>
                    )}
                </div>

                <div className="modal-body p-0">
                    <Nav tabs className="nav-primary" style={{ padding: '0 1.5rem', marginTop: '1rem', borderBottom: '1px solid #dee2e6' }}>
                        <NavItem>
                            <NavLink
                                className={activeTab === '1' ? 'active' : ''}
                                onClick={() => toggleTab('1')}
                                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px' }}
                            >
                                <Disc size={16} /> Overview
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink
                                className={activeTab === '2' ? 'active' : ''}
                                onClick={() => toggleTab('2')}
                                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px' }}
                            >
                                <FileText size={16} /> Financial & Docs
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink
                                className={activeTab === '3' ? 'active' : ''}
                                onClick={() => toggleTab('3')}
                                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px' }}
                            >
                                <Tool size={16} /> Itemized Details
                            </NavLink>
                        </NavItem>
                        {selectedMaintenance?.inspection_data && (
                            <NavItem>
                                <NavLink
                                    className={activeTab === '4' ? 'active' : ''}
                                    onClick={() => toggleTab('4')}
                                    style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px' }}
                                >
                                    <Clipboard size={16} /> Inspection Report
                                </NavLink>
                            </NavItem>
                        )}
                    </Nav>

                    <div className="p-4" style={{ backgroundColor: '#f8f9fa', minHeight: '300px' }}>
                        {selectedMaintenance && (
                            <TabContent activeTab={activeTab}>
                                <TabPane tabId="1">
                                    <div className="bg-white p-4 rounded shadow-sm">
                                        <div className="row g-4">
                                            <div className="col-md-6">
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">Branch</label>
                                                    <div className="fw-semibold">{selectedMaintenance.branch?.branch_name || 'N/A'}</div>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">Vehicle</label>
                                                    <div className="fw-semibold">
                                                        {selectedMaintenance.vehicle?.registration_number} ({selectedMaintenance.vehicle?.brand} {selectedMaintenance.vehicle?.model})
                                                    </div>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">Odometer Reading</label>
                                                    <div className="fw-semibold">{selectedMaintenance.odometer_reading ? `${selectedMaintenance.odometer_reading} km` : 'N/A'}</div>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">Service Date</label>
                                                    <div className="fw-semibold">
                                                        {selectedMaintenance?.service_date
                                                            ? selectedMaintenance.service_date.split('T')[0]
                                                            : 'N/A'}</div>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">Next Service Due</label>
                                                    <div className="fw-semibold ">{selectedMaintenance?.next_service_date
                                                        ? selectedMaintenance.next_service_date.split('T')[0]
                                                        : 'N/A'}</div>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">Type</label>
                                                    <div className="fw-semibold">{selectedMaintenance.service_type || 'N/A'}</div>
                                                </div>
                                            </div>
                                            <div className="col-12 border-top pt-3">
                                                <label className="text-muted small fw-bold text-uppercase">Description / Notes</label>
                                                <p className="mt-1 mb-0 text-dark">
                                                    {selectedMaintenance.description || 'No additional notes.'}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </TabPane>

                                <TabPane tabId="2">
                                    <div className="bg-white p-4 rounded shadow-sm">
                                        <div className="row g-4">
                                            <div className="col-md-6">
                                                <h6 className="fw-bold mb-3 text-primary">Financial Information</h6>
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">Total Cost ({config.currency})</label>
                                                    <div className="fw-bold fs-5 text-success">
                                                        {selectedMaintenance.total_cost ? ` ${selectedMaintenance.total_cost}` : 'N/A'}
                                                    </div>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">Invoice Reference</label>
                                                    <div className="fw-semibold">{selectedMaintenance.invoice_reference || 'N/A'}</div>
                                                </div>
                                                {selectedMaintenance.invoice_document && (
                                                    <div className="mt-3">
                                                        <a
                                                            href={`${mediaUrl}${selectedMaintenance.invoice_document}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="btn btn-outline-primary btn-sm"
                                                        >
                                                            <FileText size={14} className="me-2" /> View Invoice Document
                                                        </a>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="col-md-6">
                                                <h6 className="fw-bold mb-3 text-primary">Vendor Details</h6>
                                                {selectedMaintenance.parts_vendor_name && (
                                                    <div className="mb-3">
                                                        <label className="text-muted small fw-bold text-uppercase">Parts Vendor</label>
                                                        <div className="fw-semibold">{selectedMaintenance.parts_vendor_name}</div>
                                                        <div className="small text-muted">{selectedMaintenance.parts_vendor_details}</div>
                                                    </div>
                                                )}
                                                {selectedMaintenance.garage_name && (
                                                    <div className="mb-3">
                                                        <label className="text-muted small fw-bold text-uppercase">Service Center / Garage</label>
                                                        <div className="fw-semibold">{selectedMaintenance.garage_name}</div>
                                                        <div className="small text-muted">{selectedMaintenance.labour_vendor_details}</div>
                                                    </div>
                                                )}
                                                {!selectedMaintenance.parts_vendor_name && !selectedMaintenance.garage_name && (
                                                    <div className="text-muted f-st-italic">No specific vendor details recorded.</div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </TabPane>

                                <TabPane tabId="3">
                                    <div className="row g-4">
                                        <div className="col-12">
                                            {/* Parts Section */}
                                            {(parseItems(selectedMaintenance.parts_replaced).length > 0 || selectedMaintenance.parts_vendor_name) && (
                                                <Card className="mb-3 shadow-sm border-start border-primary border-3">
                                                    <CardBody className="p-3">
                                                        <H5 className="text-primary mb-3"><Disc size={18} className="me-2" />Parts Replaced</H5>
                                                        {parseItems(selectedMaintenance.parts_replaced).length > 0 ? (
                                                            <Table size="sm" striped hover responsive className="mb-0">
                                                                <thead className="table-light">
                                                                    <tr>
                                                                        <th>Part Name</th>
                                                                        <th className="text-end">Cost ({config.currency})</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {parseItems(selectedMaintenance.parts_replaced).map((item: any, index: number) => (
                                                                        <tr key={index}>
                                                                            <td>{typeof item === 'string' ? item : item.name}</td>
                                                                            <td className="text-end fw-bold">{typeof item === 'string' ? '-' : item.amount}</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </Table>
                                                        ) : <div className="text-muted f-st-italic">No itemized parts listed.</div>}
                                                    </CardBody>
                                                </Card>
                                            )}

                                            {/* Labour Section */}
                                            {(parseItems(selectedMaintenance.labour_services).length > 0 || selectedMaintenance.garage_name) && (
                                                <Card className="mb-3 shadow-sm border-start border-warning border-3">
                                                    <CardBody className="p-3">
                                                        <H5 className="text-warning mb-3"><Tool size={18} className="me-2" />Labour & Services</H5>
                                                        {parseItems(selectedMaintenance.labour_services).length > 0 ? (
                                                            <Table size="sm" striped hover responsive className="mb-0">
                                                                <thead className="table-light">
                                                                    <tr>
                                                                        <th>Service Description</th>
                                                                        <th className="text-end">Cost ({config.currency})</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {parseItems(selectedMaintenance.labour_services).map((item: any, index: number) => (
                                                                        <tr key={index}>
                                                                            <td>{item.item_description}</td>
                                                                            <td className="text-end fw-bold">{item.unit_price}</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </Table>
                                                        ) : <div className="text-muted f-st-italic">No itemized services listed.</div>}
                                                    </CardBody>
                                                </Card>
                                            )}
                                        </div>
                                    </div>
                                </TabPane>

                                {/* ── INSPECTION REPORT TAB ── */}
                                {selectedMaintenance?.inspection_data && (
                                    <TabPane tabId="4">
                                        <div className="bg-white p-4 rounded shadow-sm">
                                            {/* Header */}
                                            <div className="d-flex align-items-center gap-2 mb-4 pb-2 border-bottom">
                                                <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'linear-gradient(135deg,#e8f5e9,#c8e6c9)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <Clipboard size={20} color="#2e7d32" />
                                                </div>
                                                <div>
                                                    <h6 className="mb-0 fw-bold text-success">Inspection Report</h6>
                                                    <small className="text-muted">Inspection details recorded along with this maintenance</small>
                                                </div>
                                                <div className="ms-auto">
                                                    <Badge color={
                                                        selectedMaintenance.inspection_data.inspection_status === 'Passed' ? 'success' :
                                                            selectedMaintenance.inspection_data.inspection_status === 'Failed' ? 'danger' :
                                                                selectedMaintenance.inspection_data.inspection_status === 'In Progress' ? 'warning' : 'secondary'
                                                    } pill className="px-3 py-2" style={{ fontSize: 13 }}>
                                                        {selectedMaintenance.inspection_data.inspection_status || 'Pending'}
                                                    </Badge>
                                                </div>
                                            </div>

                                            <div className="row g-3">
                                                {/* Inspection Date */}
                                                <div className="col-md-4">
                                                    <label className="text-muted small fw-bold text-uppercase">Inspection Date</label>
                                                    <div className="fw-semibold">
                                                        {selectedMaintenance.inspection_data.inspection_date
                                                            ? new Date(selectedMaintenance.inspection_data.inspection_date).toLocaleDateString('en-GB')
                                                            : 'N/A'}
                                                    </div>
                                                </div>

                                                {/* Inspection Type */}
                                                <div className="col-md-4">
                                                    <label className="text-muted small fw-bold text-uppercase">Inspection Type</label>
                                                    <div className="fw-semibold">{selectedMaintenance.inspection_data.inspection_type || 'N/A'}</div>
                                                </div>

                                                {/* Status */}
                                                <div className="col-md-4">
                                                    <label className="text-muted small fw-bold text-uppercase">Status</label>
                                                    <div>
                                                        <Badge color={
                                                            selectedMaintenance.inspection_data.inspection_status === 'Passed' ? 'success' :
                                                                selectedMaintenance.inspection_data.inspection_status === 'Failed' ? 'danger' :
                                                                    selectedMaintenance.inspection_data.inspection_status === 'In Progress' ? 'warning' : 'secondary'
                                                        }>
                                                            {selectedMaintenance.inspection_data.inspection_status || 'Pending'}
                                                        </Badge>
                                                    </div>
                                                </div>

                                                {/* Vendor Name */}
                                                {selectedMaintenance.inspection_data.vendor_name && (
                                                    <div className="col-md-6">
                                                        <label className="text-muted small fw-bold text-uppercase">Inspector / Vendor</label>
                                                        <div className="fw-semibold">{selectedMaintenance.inspection_data.vendor_name}</div>
                                                    </div>
                                                )}

                                                {/* Vendor Details */}
                                                {selectedMaintenance.inspection_data.vendor_details && (
                                                    <div className="col-md-6">
                                                        <label className="text-muted small fw-bold text-uppercase">Vendor Details</label>
                                                        <div className="fw-semibold">{selectedMaintenance.inspection_data.vendor_details}</div>
                                                    </div>
                                                )}

                                                {/* Report Files */}
                                                {(selectedMaintenance.inspection_data.report_files || []).length > 0 && (
                                                    <div className="col-12 border-top pt-3 mt-2">
                                                        <label className="text-muted small fw-bold text-uppercase mb-2 d-block">Report Files</label>
                                                        <div className="d-flex flex-wrap gap-2">
                                                            {(selectedMaintenance.inspection_data.report_files || []).map((file: string, i: number) => (
                                                                <a
                                                                    key={i}
                                                                    href={`${mediaUrl}${file}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="btn btn-outline-success btn-sm d-flex align-items-center gap-1"
                                                                    style={{ borderRadius: 8 }}
                                                                >
                                                                    <FileText size={14} />
                                                                    Report {i + 1}
                                                                </a>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </TabPane>
                                )}
                            </TabContent>
                        )}
                    </div>
                </div>
            </Modal>
            <ToastContainer />
        </div>
    );
}

